<script setup>
import PlatformBadge from './PlatformBadge.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from './FilterSelect.vue';
import { categoryLabel, categoryColor } from '../utils/contentCategories.js';
import {
  classificationLabel,
  classificationColor,
} from '../utils/authorClassifications.js';
import { emotionLabel, emotionColor } from '../utils/emotions.js';

const props = defineProps({
  mentions: {
    type: Object,
    default: () => ({ items: [], page: 1, totalPages: 1, total: 0 }),
  },
  sortBy: { type: String, default: 'recent' },
});
const emit = defineEmits(['changePage', 'changeSort']);

const truncate = (str, n = 200) =>
  str && str.length > n ? `${str.slice(0, n)}…` : str || '';

const sentimentColor = s => {
  if (s === 'positive') return 'text-emerald-600';
  if (s === 'negative') return 'text-rose-600';
  return 'text-n-slate-11';
};

const priorityClass = score => {
  if (score === null || score === undefined) return '';
  if (score >= 70) return 'bg-rose-100 text-rose-700';
  if (score >= 40) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
};

const prevPage = () => {
  if (props.mentions.page > 1) emit('changePage', props.mentions.page - 1);
};
const nextPage = () => {
  if (props.mentions.page < props.mentions.totalPages)
    emit('changePage', props.mentions.page + 1);
};

const SORT_OPTIONS = [
  { value: 'recent', label: 'Mais recentes', icon: 'i-lucide-clock', hint: 'Ordem cronológica' },
  { value: 'priority', label: 'Mais prioritárias', icon: 'i-lucide-flame', hint: 'Maior score de prioridade' },
];
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between gap-3">
      <h3 class="text-sm font-semibold text-n-slate-12">Feed de Menções</h3>
      <div class="flex items-center gap-3">
        <FilterSelect
          :model-value="sortBy"
          :options="SORT_OPTIONS"
          icon="i-lucide-arrow-down-wide-narrow"
          placeholder="Ordenar"
          min-width="170px"
          @update:model-value="v => emit('changeSort', v)"
        />
        <span class="text-xs text-n-slate-11">{{ mentions.total }} total</span>
      </div>
    </div>

    <div
      v-if="!mentions.items.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      Nenhuma menção.
    </div>

    <div v-else class="space-y-2">
      <a
        v-for="m in mentions.items"
        :key="m.id"
        :href="m.postUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="block rounded-lg border border-n-weak p-3 transition hover:bg-n-slate-2"
      >
        <div class="mb-1 flex flex-wrap items-center gap-2">
          <span class="text-xs font-semibold text-n-slate-12">{{
            m.authorName
          }}</span>
          <PlatformBadge :platform="m.platform" size="xs" />
          <span
            class="text-[10px]"
            :class="sentimentColor(m.sentiment)"
          >
            • {{ m.sentiment || 'pending' }}
          </span>
          <span
            v-if="m.priorityScore !== null && m.priorityScore !== undefined"
            :class="priorityClass(m.priorityScore)"
            class="rounded-md px-1.5 py-0.5 text-[9px] font-semibold"
            :title="`Prioridade: ${m.priorityScore}`"
          >
            P {{ Math.round(m.priorityScore) }}
          </span>
          <span
            v-if="m.contentCategory"
            :class="categoryColor(m.contentCategory)"
            class="rounded-md px-1.5 py-0.5 text-[9px] font-medium"
          >
            {{ categoryLabel(m.contentCategory) }}
          </span>
          <span
            v-if="m.authorClassification && m.authorClassification !== 'desconhecido'"
            :class="classificationColor(m.authorClassification)"
            class="rounded-md px-1.5 py-0.5 text-[9px] font-medium"
          >
            {{ classificationLabel(m.authorClassification) }}
          </span>
          <span
            v-if="m.emotion"
            :class="emotionColor(m.emotion)"
            class="rounded-md px-1.5 py-0.5 text-[9px] font-medium"
          >
            {{ emotionLabel(m.emotion) }}
          </span>
          <span class="ml-auto text-[10px] text-n-slate-11">{{ m.type }}</span>
        </div>
        <p class="text-xs text-n-slate-11">{{ truncate(m.content) }}</p>
        <div class="mt-1 flex gap-3 text-[10px] text-n-slate-11">
          <span>♥ {{ m.likes }}</span>
          <span>💬 {{ m.comments }}</span>
          <span>↗ {{ m.shares }}</span>
        </div>
      </a>
    </div>

    <div class="mt-3 flex items-center justify-between text-xs text-n-slate-11">
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-chevron-left"
        :disabled="mentions.page <= 1"
        @click="prevPage"
      >
        Anterior
      </Button>
      <span>Página {{ mentions.page }} / {{ mentions.totalPages }}</span>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-chevron-right"
        trailing-icon
        :disabled="mentions.page >= mentions.totalPages"
        @click="nextPage"
      >
        Próxima
      </Button>
    </div>
  </div>
</template>
